import * as React from "react";
import { useMemo } from "react";

import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
} from "@/components/Strategies/StrategyPineSectionCard";
import { Badge } from "@/components/ui/badge";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { PolicyKindChips } from "@/components/Policy/PolicyKindChips";
import { useTradePolicyFilterCatalogFields } from "@/hooks/useTradePolicyFilterCatalogFields";
import { resolvePolicyKinds } from "@/utils/policyKindChips";

import { buildPolicyViewNaturalLanguageSummary } from "../ExecutionRulesSimulator/policyTreeUtils";
import { PolicySimulatorViewer } from "../ExecutionRulesSimulator/PolicySimulatorViewer";
import { PolicyViewSummaryContent } from "./PolicyViewSummaryContent";
import { strategiesMainCardClassName } from "./strategiesCardShell";
import type { ReportStrategyPolicyVersion } from "../../../types/reports";

export type StrategyPolicyViewBodyProps = {
  title: string;
  policyTree: unknown;
  policyVersions?: ReportStrategyPolicyVersion[];
  /** Active version — used for BE policy_kinds chips when present. */
  policyVersion?: ReportStrategyPolicyVersion | null;
  /** When false, policy segments stay flush (workspace sheet). Default true for dialog cards. */
  segmentChrome?: boolean;
  /** Show an Archived badge on the summary card. */
  isArchived?: boolean;
  className?: string;
};

export function StrategyPolicyViewBody({
  title,
  policyTree,
  policyVersions = [],
  policyVersion = null,
  segmentChrome = true,
  isArchived = false,
  className,
}: StrategyPolicyViewBodyProps): React.ReactElement {
  const displayTitle = title.trim() || "version";
  const catalog = useTradePolicyFilterCatalogFields();
  const summaryDocument = useMemo(() => {
    if (catalog.catalogLoading || catalog.catalogError) return null;
    return buildPolicyViewNaturalLanguageSummary(
      policyTree,
      catalog.fieldsByPath,
      policyVersions
    );
  }, [
    catalog.catalogError,
    catalog.catalogLoading,
    catalog.fieldsByPath,
    policyTree,
    policyVersions,
  ]);

  const policyKinds = useMemo(
    () =>
      resolvePolicyKinds({
        policyKinds: policyVersion?.policy_kinds ?? policyVersion?.policyKinds,
        entryActions: policyVersion?.entry_actions ?? policyVersion?.entryActions,
        exitLegs: policyVersion?.exit_legs ?? policyVersion?.exitLegs,
        policyTree,
      }),
    [policyTree, policyVersion]
  );

  return (
    <div
      data-policy-view-body=""
      className={cn("box-border flex w-full min-w-0 flex-col gap-4", className)}
    >
      <div
        data-policy-view-summary=""
        className={cn(strategiesMainCardClassName, "gap-1.5 border-solid")}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <FieldLabel className={cn(STRATEGY_PINE_SECTION_LABEL_CLASS, "m-0")}>
            Summary
          </FieldLabel>
          <PolicyKindChips kinds={policyKinds} />
          {isArchived ? (
            <Badge variant="secondary" className="font-[family-name:var(--font-primary)] font-normal">
              Archived
            </Badge>
          ) : null}
        </div>
        <Field className="gap-0">
          {catalog.catalogLoading ? (
            <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              Loading policy summary…
            </FieldDescription>
          ) : catalog.catalogError ? (
            <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              Could not load field labels for this summary.
            </FieldDescription>
          ) : summaryDocument ? (
            <div data-slot="field-description" className={STRATEGY_PINE_SECTION_DESC_CLASS}>
              <PolicyViewSummaryContent document={summaryDocument} />
            </div>
          ) : null}
        </Field>
      </div>

      <PolicySimulatorViewer
        policyTree={policyTree}
        versionLabel={displayTitle}
        policyVersions={policyVersions}
        catalog={catalog}
        segmentChrome={segmentChrome}
      />
    </div>
  );
}
